import { useState, useCallback, useMemo, useRef } from "react";
import JSZip from "jszip";
import { ToolContainer, FileDropZone, ProcessingState, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { convertImageDetailed } from "./convertBrowser";
import { formatInfo, parseConvertOptions } from "./convertCore";
import { DEFAULT_FORM, applyLongestSide, bulkOutputName, bulkRowDetail, formToRaw, friendlyOptionError, needsFill, uniqueName, type ConvertForm } from "./convertForm";
import { FillNotice, FormatPicker, FormatSpecificControls, MoreOptions, QualityControl, formatUnavailable, optionError as formOptionError, useEncodeSupport } from "./ConvertOptionsPanel";

// ExifRemover builds its ZIP names with this.
export { uniqueName };

interface Row { name: string; status: "waiting" | "done" | "failed"; detail: string }

const TOOL_INFO_CONTENT = {
  what: `Applies the same format conversion and settings to many images at once, instead of processing them one at a time. It writes PNG, JPEG, WebP, AVIF, GIF, TIFF, BMP and ICO, and each file can also be resized, turned or flipped, made black and white, given a print resolution, and have its camera and location details removed or kept. Everything downloads as a single ZIP file with every processed image inside, rather than triggering a separate download prompt per file.`,
  how: `Upload a batch of images, choose the output format and quality you want applied to all of them, open "More options" for size, turning, colour and detail settings, and press Process All. One ZIP with every processed file downloads when it finishes, and the list shows what happened to each picture.`,
  tip: `For genuinely large batches, keep an eye on your browser's memory usage, since everything processes locally and extremely large batches can slow down on lower-powered devices. Set "Longest side" to shrink every picture to a maximum size (pictures already smaller are left alone), or use width, height and a fit mode for exact sizes. JPEG and 24-bit BMP cannot be see-through, so transparent areas get the fill colour you choose.`,
};

export default function BulkImage() {
  const [files, setFiles] = useState<File[]>([]);
  const [form, setForm] = useState<ConvertForm>({ ...DEFAULT_FORM, format: "jpeg", quality: 80 });
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [maxSide, setMaxSide] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState("");
  const cancelledRef = useRef(false);
  const { logUsageNow } = useLogUsageOnAction();
  const canEncode = useEncodeSupport();

  const change = useCallback((patch: Partial<ConvertForm>) => { setForm((f) => ({ ...f, ...patch })); setRows([]); setSummary(""); }, []);
  const parsed = useMemo(() => parseConvertOptions(applyLongestSide(formToRaw(form), maxSide)), [form, maxSide]);
  const optionError = formOptionError(form, parsed.ok ? null : friendlyOptionError(parsed.error));
  const blocked = !parsed.ok || !!optionError || formatUnavailable(canEncode, form.format);
  const mayHaveAlpha = files.some((f) => /\.(png|webp|gif|svg|avif)$/i.test(f.name) || /^image\/(png|webp|gif|svg\+xml|avif)$/.test(f.type));

  const handleFiles = useCallback((newFiles: File[]) => {
    setFiles(prev => [...prev, ...newFiles]);
    setRows([]); setSummary("");
  }, []);

  const handleStop = () => {
    cancelledRef.current = true;
  };

  const process = async () => {
    if (files.length === 0 || !parsed.ok) return;
    const options = parsed.options;
    // Logged here -- the actual batch-processing action -- rather than
    // on each file drop, matching the same fix already applied to
    // Merge PDF, Audio Merger, and Merge Video: adding images across
    // separate drops no longer burns extra free uses for what's still
    // just one unfinished batch job.
    const allowed = await logUsageNow();
    if (!allowed) return;
    cancelledRef.current = false;
    setProcessing(true);
    setProgress(0);
    try {
      const zip = new JSZip();
      const used = new Set<string>();
      const out: Row[] = [];
      let okCount = 0, processedCount = 0;
      for (const file of files) {
        if (cancelledRef.current) break; // real, responsive stop -- checked between each file
        try {
          const { blob, width, height } = await convertImageDetailed(file, options);
          const name = bulkOutputName(file.name, options.format, used);
          zip.file(name, blob);
          okCount++;
          out.push({ name, status: "done", detail: bulkRowDetail(file.size, blob.size, options.format === "ico" ? `sizes ${options.sizes.join(", ")} px` : `${width} x ${height} px`) });
        } catch (e) {
          out.push({ name: file.name, status: "failed", detail: (e as Error)?.message || "could not be read" });
        }
        processedCount++;
        setProgress((processedCount / files.length) * 100);
      }
      setRows(out);
      setSummary(`${files.length} in, ${okCount} out${okCount < files.length ? ` (${files.length - okCount} skipped - see the list)` : ""}`);
      if (okCount > 0) {
        const zipBlob = await zip.generateAsync({ type: "blob" });
        downloadBlob(zipBlob, "bulk-processed-images.zip");
      }
      if (cancelledRef.current) {
        toast.info(`Stopped after ${processedCount} of ${files.length} image(s) — the ones already processed are in the downloaded ZIP`);
      } else if (okCount === files.length) {
        toast.success(`Processed ${okCount} images! Downloaded as one ZIP.`);
      } else {
        toast.warning(`${okCount} of ${files.length} images processed - ${files.length - okCount} could not be read`);
      }
    } catch (e) { toast.error((e as Error)?.message || "Processing failed"); }
    finally { setProcessing(false); setProgress(0); }
  };

  return (
    <ToolContainer>
      <FileDropZone accept="image/*" multiple onFiles={handleFiles} label="Drop multiple images" description="Process all images at once" deferUsageLog />
      {files.length > 0 && (
        <div className="mt-6 space-y-4">
          <p className="text-sm font-medium">{files.length} image(s) selected</p>
          <ul className="max-h-48 overflow-auto text-xs space-y-1 border rounded-md p-2" data-testid="bulk-list">
            {files.map((f, i) => {
              return <li key={i} className="flex justify-between gap-2"><span className="truncate">{f.name}</span><span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span></li>;
            })}
          </ul>
          <FormatPicker form={form} onChange={change} support={canEncode} />
          <QualityControl form={form} onChange={change} min={10} />
          <FormatSpecificControls form={form} onChange={change} />
          <FillNotice form={form} onChange={change} show={needsFill(form) && mayHaveAlpha} many />
          <MoreOptions
            form={form}
            onChange={change}
            metaHelp="Copied into each new JPEG that came from a JPEG. For every other file this information is removed."
            sizeExtra={
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="bulk-longest" className="text-xs">Longest side (optional, px)</Label>
                <Input id="bulk-longest" type="number" min="0" placeholder="keep original size" value={maxSide} onChange={(e) => { setMaxSide(e.target.value); setRows([]); setSummary(""); }} />
                <p className="text-xs text-muted-foreground">Shrinks every picture so its longest side is at most this. Used when width and height are empty.</p>
              </div>
            }
          />
          {optionError && <p role="alert" className="text-sm text-destructive">{optionError}</p>}
          {summary && <p className="text-sm font-medium" data-testid="bulk-summary">{summary}</p>}
          {rows.length > 0 && (
            <ul className="max-h-48 overflow-auto text-xs space-y-1 border rounded-md p-2" data-testid="bulk-results">
              {rows.map((r, i) => <li key={i} className={r.status === "failed" ? "text-red-600" : ""}>{r.status === "failed" ? "Skipped" : "Done"}: {r.name} - {r.detail}</li>)}
            </ul>
          )}
          <div className="flex gap-3">
            {processing ? <ProcessingState message="Processing images..." progress={progress} onStop={handleStop} /> : <Button onClick={process} disabled={blocked}>Process All</Button>}
            <Button variant="outline" onClick={() => { setFiles([]); setRows([]); setSummary(""); }}>Clear</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
